/**
 * Event system for CopilotContext
 * Handles events like rollbacks that need to communicate with other components
 */

export type CopilotRollbackEvent = {
  type: 'rollback'
  data: {
    content: string
    files?: { url: string; filename: string; ext: string }[]
  }
}

export type CopilotOpenBillingModalEvent = {
  type: 'openBillingModal'
  data: {
    highlightedPlanId?: string
  }
}

export type CopilotExecutionCompletedEvent = {
  type: 'executionCompleted'
  data: {
    executionId: string
    workflowRef?: string
  }
}

import type { RepeatSpec } from 'sprites-types/api/v2/scheduled-tasks'

export type ScheduleTaskPrefill = {
  name: string
  description?: string | null
  toolCalls: Array<{
    function: {
      name: string
      arguments: Record<string, unknown>
    }
  }>
  repeatSpec?: RepeatSpec | null
  timezone?: string | null
  maxRuns?: number | null
}

export type CopilotOpenScheduleModalEvent = {
  type: 'openScheduleModal'
  data: {
    toolCallId: string
    prefill: ScheduleTaskPrefill
  }
}

export type CopilotEvent =
  | CopilotRollbackEvent
  | CopilotOpenBillingModalEvent
  | CopilotExecutionCompletedEvent
  | CopilotOpenScheduleModalEvent

type CopilotEventListener<T extends CopilotEvent = CopilotEvent> = (event: T) => void

class CopilotEventEmitter {
  private listeners: CopilotEventListener[] = []

  addEventListener(listener: CopilotEventListener): () => void {
    this.listeners.push(listener)

    // Return cleanup function
    return () => {
      const index = this.listeners.indexOf(listener)
      if (index > -1) {
        this.listeners.splice(index, 1)
      }
    }
  }

  emit(event: CopilotEvent): void {
    this.listeners.forEach((listener) => {
      try {
        listener(event)
      } catch (error) {
        console.error('Error in copilot event listener:', error)
      }
    })
  }

  // Type-safe helpers for specific events
  emitRollback(content: string, files?: { url: string; filename: string; ext: string }[]): void {
    this.emit({
      type: 'rollback',
      data: { content, files },
    })
  }

  emitOpenBillingModal(highlightedPlanId?: string): void {
    this.emit({
      type: 'openBillingModal',
      data: { highlightedPlanId },
    })
  }

  emitExecutionCompleted(executionId: string, workflowRef?: string): void {
    this.emit({
      type: 'executionCompleted',
      data: { executionId, workflowRef },
    })
  }

  emitOpenScheduleModal(toolCallId: string, prefill: ScheduleTaskPrefill): void {
    this.emit({
      type: 'openScheduleModal',
      data: { toolCallId, prefill },
    })
  }
}

// Global singleton instance
export const copilotEvents = new CopilotEventEmitter()
